"use client";

import type { VillaCard } from "../domain/api";
import { criterionLabel, t, type UiLang } from "./i18n";

function formatMoney(amount: number, currency: string, lang: UiLang): string {
  try {
    return new Intl.NumberFormat(lang, { style: "currency", currency, maximumFractionDigits: 0 }).format(amount);
  } catch {
    return `${amount} ${currency}`;
  }
}

export function ResultCard({
  card,
  href,
  lang,
  position,
  onOpen,
}: {
  card: VillaCard;
  /** The existing villamine.com villa page, in the site locale. */
  href: string;
  lang: UiLang;
  position: number;
  onOpen: (card: VillaCard, position: number) => void;
}) {
  const facts = [
    card.capacity !== null ? `${card.capacity} ${t(lang, "guests")}` : null,
    card.bedrooms !== null ? `${card.bedrooms} ${t(lang, "bedrooms")}` : null,
    card.bathrooms !== null ? `${card.bathrooms} ${t(lang, "bathrooms")}` : null,
  ].filter(Boolean);

  const price = card.price
    ? card.price.kind === "quote"
      ? t(lang, "price_total", { amount: formatMoney(card.price.amount, card.price.currency, lang), nights: card.price.nights ?? 0 })
      : t(lang, "price_from_night", { amount: formatMoney(card.price.amount, card.price.currency, lang) })
    : t(lang, "price_on_page");

  const availability = card.availability;
  const availabilityClass =
    availability?.status === "AVAILABLE" ? "is-available" : availability?.status === "UNAVAILABLE" ? "is-unavailable" : "is-unknown";
  const availabilityLabel =
    availability?.status === "AVAILABLE"
      ? t(lang, "available")
      : availability?.status === "UNAVAILABLE"
        ? t(lang, "unavailable")
        : availability
          ? t(lang, "not_verified")
          : null;

  // Tolerate payloads persisted by an older widget build.
  const matched = card.matched ?? [];
  const matchedDerived = card.matched_derived ?? [];
  const unconfirmed = (card.unconfirmed ?? []).filter((k) => k !== "dates" && k !== "budget");

  return (
    <article className="vmai-card">
      <a className="vmai-card__media" href={href} target="_blank" rel="noopener" onClick={() => onOpen(card, position)} aria-label={card.name}>
        {card.image ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img src={card.image.thumbnail_url} alt="" loading="lazy" />
        ) : (
          <span className="vmai-card__noimg" />
        )}
      </a>
      <div className="vmai-card__body">
        <div className="vmai-card__top">
          <h4 className="vmai-card__name">{card.name}</h4>
          {availabilityLabel && <span className={`vmai-badge ${availabilityClass}`}>{availabilityLabel}</span>}
        </div>
        <p className="vmai-card__place">
          {card.region}
          {card.locality ? ` · ${card.locality}` : ""}
        </p>
        <p className="vmai-card__facts">{facts.join(" · ")}</p>
        {(matched.length > 0 || matchedDerived.length > 0 || unconfirmed.length > 0) && (
          <ul className="vmai-card__tags">
            {matched.map((a) => (
              <li key={a} className="vmai-tag is-confirmed">
                {criterionLabel(lang, a)}
              </li>
            ))}
            {matchedDerived.map((a) => (
              <li key={a} className="vmai-tag is-derived" title={t(lang, "derived_title")}>
                {criterionLabel(lang, a)} · {t(lang, "derived")}
              </li>
            ))}
            {unconfirmed.map((a) => (
              <li key={a} className="vmai-tag is-unconfirmed">
                {criterionLabel(lang, a)} · {t(lang, "not_confirmed")}
              </li>
            ))}
          </ul>
        )}
        <div className="vmai-card__bottom">
          <span className="vmai-card__price">{price}</span>
          <a className="vmai-card__link" href={href} target="_blank" rel="noopener" onClick={() => onOpen(card, position)}>
            {t(lang, "view_villa")} →
          </a>
        </div>
      </div>
    </article>
  );
}
